import moment, { min } from "moment";
import { platform } from "os";
import { CardType, MAC_CardType } from "src/components/shared-components/enum/enumCard";
import { CustomerStaffType } from "src/components/shared-components/enum/enumCustomerStaff";
import { GenericTag } from "./generic-tag";

/**
 * This class handle generic ABB Millium tag.
 * It following this standard:
 *  u08_t code[2];             Key Code
    u08_t group[1];           Group
    u08_t type[1];              Type (Customer/Emplyee)
    u08_t plt_code[3];     Plant Code
    u08_t year_expiry[1];               Scadenza ann
    u08_t month_expiry[1];          Scadenza mese
    u08_t day_expiry[1];                Scadenza giorno
    u08_t hour_expiry[1];              Scadenza ora
    u08_t min_expiry[1];                Scadenza minuti
    u08_t pos_conf[1];                    Configurazione delpos (Legacy)
    u08_t credit[3];                           Credito (Legacy)
 */
export class ABBMilleniumTag extends GenericTag {
  constructor() {
    super();
  }

  /**
   * Unpack a block and set expiration date,plantCode and cardNumber
   *
   * @param blockData   block that we have read
   * @param uid     tag UID
   */
  public unpack(blockData: string, uid: string): void {
    this._uid = uid;
    this._expirationDate = this.unpackExpirationDate(blockData);
    this._plantCode = this.unpackPlantCode(blockData);
    this._cardNumber = this.unpackCardNumber(blockData);
  }

  /**
   * Pack ABB millenium data to byte array string data
   *
   * @param cardNumber Card number to write
   * @param cardType Card type to write
   * @param plantCode plant code to write
   * @param expirationDate Expiration date
   * @returns
   */
  public pack(
    cardNumber: number,
    cardType: CardType,
    plantCode: number,
    expirationDate: string
  ): string {
    let block: string = "";

    block += ("0000" + cardNumber.toString(16)).slice(-4); // card number
    block += "00"; //type
    block += ("00" + this.getMACCardTypeByte(cardType).toString(16)).slice(-2); //group
    block += ("000000" + parseInt(plantCode.toString()).toString(16)).slice(-6);
    const arExDate: string[] = expirationDate.substring(0, 10).split("/");
    const gg: string = ("00" + parseInt(arExDate[0], 10).toString(16)).slice(
      -2
    );
    const mm: string = ("00" + parseInt(arExDate[1], 10).toString(16)).slice(
      -2
    );
    const yy: string = (
      "00" + parseInt(arExDate[2].slice(-2), 10).toString(16)
    ).slice(-2);
    block += yy + mm + gg;
    const arExTime: string[] = expirationDate.substring(11).split(":");
    const hh: string = (
      "00" + parseInt(arExTime[0].trim(), 10).toString(16)
    ).slice(-2);
    const min: string = (
      "00" + parseInt(arExTime[1].trim(), 10).toString(16)
    ).slice(-2);
    block += hh + min;
    block += "04"; // pos type
    block += "000000"; // credit

    return block.toUpperCase();
  }

  /**
   * Unpack expiration date
   *
   * @param blockData Block data to unpack
   * @returns
   */
  private unpackExpirationDate(blockData: string): string {
    const year: string = parseInt(blockData.substring(14, 16), 16).toString();
    const month: string = (
      "0" + parseInt(blockData.substring(16, 18), 16).toString()
    ).slice(-2);
    const day: string = (
      "0" + parseInt(blockData.substring(18, 20), 16).toString()
    ).slice(-2);
    const hour: string = (
      "0" + parseInt(blockData.substring(20, 22), 16).toString()
    ).slice(-2);
    const minutes: string = (
      "0" + parseInt(blockData.substring(22, 24), 16).toString()
    ).slice(-2);

    return day + "/" + month + "/" + year + " " + hour + ":" + minutes;
  }

  /**
   * Extract plant code from original byte array
   *
   * @param blockData block data to get decoded plant code
   * @returns
   */
  private unpackPlantCode(blockData: string): number {
    return parseInt(blockData.substring(8, 14), 16);
  }

  /**
   * Extract card number from original byte array data
   *
   * @param blockData block data to get decoded card number
   * @returns
   */
  private unpackCardNumber(blockData: string): number {
    return parseInt(blockData.substring(0, 4), 16);
  }

  /**
   *Get ABB MAC card type
   *
   * @param cardType Card type for abb card
   * @returns
   */
  private getMACCardTypeByte(cardType: CardType) {
    let macCardType: MAC_CardType = MAC_CardType.CUSTOMER;

    switch (cardType) {
      case CardType.GUEST:
        macCardType = MAC_CardType.CUSTOMER;
        break;
      case CardType.SERVICE:
        macCardType = MAC_CardType.SERVICE;
        break;
    }

    return macCardType;
  }
}
